import React, { useEffect, useState } from "react";
import axios from "axios";
import { MdDelete } from "react-icons/md";
import { Icon } from "organisms/iconCommonent";
import { FaRegEdit } from "react-icons/fa";
import { AiOutlinePlusCircle } from "react-icons/ai";

type Impact = {
  _id?: string;
  title: string;
  percentage: string;
  description: string;
};

export type ApiContent = {
  heading: string;
  description: string;
};

const Fellowship: React.FC = React.memo(() => {
  const [impactList, setImpactList] = useState<Impact[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCard, setEditingCard] = useState<Impact | null>(null);
  const [apiContent, setApiContent] = useState<ApiContent | null>(null);
  const [showContentForm, setShowContentForm] = useState(false); // ✅ for section edit

  // Fetch data
  const fetchImpact = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/shaping-change-makers`
      );
      const content = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/content/68efb02504bdd5cc2b26138d`
      );
    
      if (content.data.status === "success") {
        setApiContent(content.data.data);
      }

      if (res.data.status === "success") {
        setImpactList(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchImpact();
  }, []);

  // Delete card
  const handleDelete = async (id: string) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_LOCAL_URL}/api/shaping-change-makers/${id}`
      );
      fetchImpact();
    } catch (error) {
      console.error("Error deleting card:", error);
    }
  };

  // Edit or Add Card
  const handleEdit = (item?: Impact) => {
    setEditingCard(item || null);
    setShowForm(true);
  };

  return (
    <section className="pt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-lato-regular">Section - 03</h2>
        <hr />
      </div>

      <div className="w-full bg-white border border-gray-200 rounded-xl overflow-hidden p-4">
        <div className="md:flex items-center justify-between border-b border-[#84848480] border-opacity-50">
          <h4 className="pb-3 font-lato-bold text-[#07393C]">
            {apiContent?.heading}
          </h4>
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded"
            onClick={() => setShowContentForm(true)} // ✅ Open section edit modal
          >
            <Icon icon={FaRegEdit} />
          </button>
        </div>

        <h6
          className="font-lato-regular md:w-[90%] xl:my-8 my-4"
          dangerouslySetInnerHTML={{ __html: apiContent?.description ?? "" }}
        />

        {/* Add New Card Button */}
        <div className="flex justify-end">
          <button
            className="bg-darkCyan flex items-center text-white font-lato-regular rounded-md px-3 py-2"
            onClick={() => handleEdit()}
          >
            <span className="text-2xl pr-2">
              <Icon icon={AiOutlinePlusCircle} />
            </span>
            <h6>Add new card</h6>
          </button>
        </div>

        {/* Cards */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {impactList.map((elem) => (
            <div
              key={elem._id}
              className="h-[250px] py-4 flex flex-col justify-between border-2 border-black/10 rounded-lg px-3"
            >
              <h6 className="font-lato-bold text-lg">{elem.title}</h6>
              <div>
                {elem.percentage && (
                  <h6 className="text-2xl font-lato-bold">{elem.percentage}</h6>
                )}
                <h6 className="mt-2">{elem.description}</h6>
              </div>
              <div className="flex justify-start gap-3 mt-2">
                <button
                  onClick={() => handleEdit(elem)}
                  className="bg-blue-500 text-white px-2 py-1 rounded"
                >
                  <Icon icon={FaRegEdit} />
                </button>
                <button
                  onClick={() => handleDelete(elem._id!)}
                  className="text-red-500 border border-red-500 px-2 py-1 rounded"
                >
                  <Icon icon={MdDelete} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Impact Card Modal */}
      {showForm && (
        <ImpactFormModal
          onClose={() => setShowForm(false)}
          fetchImpact={fetchImpact}
          editingCard={editingCard}
        />
      )}

      {/* Section Content Edit Modal */}
      {showContentForm && apiContent && (
        <SectionContentModal
          onClose={() => setShowContentForm(false)}
          apiContent={apiContent}
          fetchImpact={fetchImpact}
        />
      )}
    </section>
  );
});

export default Fellowship;

//
// ==================== IMPACT FORM MODAL ====================
//
interface ImpactFormModalProps {
  onClose: () => void;
  fetchImpact: () => void;
  editingCard: Impact | null;
}

const ImpactFormModal = ({
  onClose,
  fetchImpact,
  editingCard,
}: ImpactFormModalProps) => {
  const [title, setTitle] = useState(editingCard?.title || "");
  const [percentage, setPercentage] = useState(editingCard?.percentage || "");
  const [description, setDescription] = useState(
    editingCard?.description || ""
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = { title, percentage, description };

    try {
      if (editingCard?._id) {
        await axios.patch(
          `${process.env.REACT_APP_LOCAL_URL}/api/shaping-change-makers/${editingCard._id}`,
          payload
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_LOCAL_URL}/api/shaping-change-makers`,
          payload
        );
      }
      fetchImpact();
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to save data");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
        <h3 className="text-lg font-lato-bold mb-4">
          {editingCard ? "Edit Card" : "Add New Card"}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block font-lato-bold">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
          </div>

          <div>
            <label className="block font-lato-bold">Percentage</label>
            <input
              type="text"
              value={percentage}
              onChange={(e) => setPercentage(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          <div>
            <label className="block font-lato-bold">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 h-24"
              required
            />
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {editingCard ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

//
// ==================== SECTION CONTENT MODAL ====================
//
interface SectionContentModalProps {
  onClose: () => void;
  apiContent: ApiContent;
  fetchImpact: () => void;
}

const SectionContentModal = ({
  onClose,
  apiContent,
  fetchImpact,
}: SectionContentModalProps) => {
  const [heading, setHeading] = useState(apiContent.heading);
  const [description, setDescription] = useState(apiContent.description);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${process.env.REACT_APP_LOCAL_URL}/api/content/68eccf219b4d8bb484b69e3b`,
        { heading, description }
      );
      fetchImpact();
      onClose();
    } catch (error) {
      console.error("Error updating section:", error);
      alert("Failed to update section content");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
        <h3 className="text-lg font-lato-bold mb-4">Edit Section Content</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block font-lato-bold">Heading</label>
            <input
              type="text"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
          </div>

          <div>
            <label className="block font-lato-bold">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 h-24"
              required
            />
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-teal text-white rounded-md "
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
